import React, { FC, Fragment, useState } from 'react';
import { Alert, Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ActionPriceList } from 'models/action';
import { useTranslation } from 'react-i18next';
import { downloadActionPriceListPdf } from 'components/order-details/main-order-view/document-print-actions/files';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { LoadingButton } from '@mui/lab';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { buildPriceListPdfFileName } from 'utils/file';

type ActionPriceListsPageActionsProps = {
    onNewActionListClick: () => void;
    actionPriceLists: ActionPriceList[];
    actionPriceList: ActionPriceList;
    onSelectedPriceList: (selectedActionList: string) => void;
};
const ActionPriceListsPageActions: FC<ActionPriceListsPageActionsProps> = ({
    onNewActionListClick,
    actionPriceLists,
    actionPriceList,
    onSelectedPriceList,
}: ActionPriceListsPageActionsProps) => {
    const { t: translate } = useTranslation(['common', 'lab-settings']);
    const [selectedPriceList, setSelectedPriceList] = useState('');
    const [spinner, setSpinner] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const handlePriceListChange = (event: SelectChangeEvent<string>) => {
        setSelectedPriceList(event.target.value);
        onSelectedPriceList(event.target.value);
    };
    const getDefaultActionPriceListName = () => {
        return actionPriceLists.find((list) => list.isStandardPriceList)?.actionPriceListName;
    };

    const handleActionPriceListPdf = async () => {
        try {
            setError(null);
            setSpinner(true);
            await downloadActionPriceListPdf(
                buildPriceListPdfFileName(currentOrgVar().name, actionPriceList.actionPriceListName),
                actionPriceList._id as string,
            );
        } catch (error) {
            setError(error as Error);
        } finally {
            setSpinner(false);
        }
    };

    return (
        <Fragment>
            <Box mb={3} display="flex" alignItems="center">
                {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
                <Grid container spacing={1}>
                    <Grid item sm={9} xs={12} sx={{ visibility: !actionPriceLists.length ? 'hidden' : 'visible' }}>
                        <FormControl style={{ minWidth: 250 }}>
                            <InputLabel>{translate('lab-settings:PRICE_LISTS')}</InputLabel>
                            <Select
                                value={
                                    actionPriceList.actionPriceListName ||
                                    getDefaultActionPriceListName() ||
                                    selectedPriceList
                                }
                                label={translate('lab-settings:PRICE_LISTS')}
                                onChange={handlePriceListChange}
                            >
                                {actionPriceLists.map((list) => (
                                    <MenuItem key={list._id} value={list.actionPriceListName}>
                                        {list.isStandardPriceList
                                            ? `${list.actionPriceListName} (${translate(
                                                  'common:DEFAULT',
                                              ).toLowerCase()})`
                                            : list.actionPriceListName}{' '}
                                        - {list.currency}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Box>
                    <LoadingButton
                        loading={spinner}
                        sx={{ whiteSpace: 'nowrap', marginRight: 1 }}
                        disabled={!actionPriceList._id}
                        onClick={handleActionPriceListPdf}
                        startIcon={<PictureAsPdfIcon />}
                    >
                        {translate('common:EXPORT_PDF')}
                    </LoadingButton>
                </Box>
                <Box>
                    <Button
                        sx={{ whiteSpace: 'nowrap' }}
                        variant="contained"
                        onClick={onNewActionListClick}
                        startIcon={<AddIcon />}
                    >
                        {translate('lab-settings:NEW_PRICE_LIST')}
                    </Button>
                </Box>
            </Box>
            {!actionPriceLists.length && (
                <Box mt={2}>
                    <Alert severity="info">{translate('lab-settings:NO_PRICE_LISTS')}</Alert>
                </Box>
            )}
        </Fragment>
    );
};

export default ActionPriceListsPageActions;
